import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import doctorTeleConsultationApi from "./doctorTeleConsultationApi";
import "../doctor-teleconsultation/DoctorTeleConsultationPage.css";

const DoctorTeleConsultationsPage = () => {
  const navigate = useNavigate();

  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchConsultations = useCallback(async () => {
    try {
      setError("");

      const response =
        await doctorTeleConsultationApi.getConsultations();

      setConsultations(response?.consultations || []);
    } catch (err) {
      console.error(
        "Failed to load doctor consultations:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Failed to load teleconsultations."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchConsultations();
  }, [fetchConsultations]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshing(true);
      fetchConsultations();
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchConsultations]);

  const handleOpen = (consultation) => {
    if (!consultation?.consultation_id) {
      return;
    }

    navigate(
      `/doctor/tele-consultations/${consultation.consultation_id}`
    );
  };

  const handleManualRefresh = () => {
    if (refreshing) return;

    setRefreshing(true);
    fetchConsultations();
  };

  const handleLogout = () => {
    // Clear authentication/session data
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");

    // Clear doctor-related session data if present
    localStorage.removeItem("doctorName");
    localStorage.removeItem("userRole");

    sessionStorage.clear();

    // Redirect to login
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="doctor-tele-page">
        <div className="doctor-loading-container">
          <div className="doctor-spinner"></div>
          <p>Loading teleconsultations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-tele-page">

      {/* Header */}
      <div className="doctor-tele-header">

        <div className="doctor-tele-title-section">

          <div className="doctor-page-icon">
            ☎
          </div>

          <div>
            <h1>Teleconsultations</h1>

            <p>
              Manage waiting and ongoing rural health
              consultations.
            </p>
          </div>

        </div>

        {/* Header Actions */}
        <div className="doctor-header-actions">

          {/* Refresh */}
          <button
            type="button"
            className="doctor-refresh-button"
            onClick={handleManualRefresh}
            disabled={refreshing}
          >
            <span
              className={
                refreshing
                  ? "refresh-icon spinning"
                  : "refresh-icon"
              }
            >
              ↻
            </span>

            {refreshing ? "Refreshing..." : "Refresh"}
          </button>

          {/* Logout */}
          <button
            type="button"
            className="doctor-logout-button"
            onClick={handleLogout}
          >
            <span className="logout-icon">
              ↪
            </span>

            Logout
          </button>

        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="doctor-error">
          <span className="error-icon">!</span>
          <span>{error}</span>
        </div>
      )}

      {/* Empty State */}
      {consultations.length === 0 ? (
        <div className="doctor-empty">

          <div className="doctor-empty-icon">
            ☎
          </div>

          <h3>No active consultations</h3>

          <p>
            Waiting and ongoing consultations will
            appear here automatically.
          </p>

          <button
            type="button"
            className="doctor-empty-refresh"
            onClick={handleManualRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Checking..." : "Check Again"}
          </button>

        </div>
      ) : (
        <div className="doctor-consultation-list">

          {consultations.map((consultation) => {

            const status =
              consultation?.status?.toUpperCase() ||
              "UNKNOWN";

            const isOngoing = status === "ONGOING";

            return (
              <div
                key={consultation.consultation_id}
                className="doctor-consultation-card"
              >

                {/* Card Header */}
                <div className="consultation-card-top">

                  <div className="patient-heading">

                    <div className="patient-avatar">
                      {(
                        consultation.member?.name ||
                        "P"
                      )
                        .charAt(0)
                        .toUpperCase()}
                    </div>

                    <div>
                      <h2>
                        {consultation.member?.name ||
                          "Patient"}
                      </h2>

                      <p>
                        {consultation.member?.member_id ||
                          "Member ID unavailable"}
                      </p>
                    </div>

                  </div>

                  <span
                    className={`status-badge status-${status.toLowerCase()}`}
                  >
                    <span className="status-dot"></span>
                    {status}
                  </span>

                </div>

                {/* Information */}
                <div className="consultation-info-grid">

                  <div className="consultation-info-item">
                    <span>Family ID</span>
                    <strong>
                      {consultation.family?.family_id || "-"}
                    </strong>
                  </div>

                  <div className="consultation-info-item">
                    <span>Age</span>
                    <strong>
                      {consultation.member?.age ?? "-"}
                    </strong>
                  </div>

                  <div className="consultation-info-item">
                    <span>Gender</span>
                    <strong>
                      {consultation.member?.gender || "-"}
                    </strong>
                  </div>

                  <div className="consultation-info-item">
                    <span>Mobile</span>
                    <strong>
                      {consultation.member?.mobile || "-"}
                    </strong>
                  </div>

                  <div className="consultation-info-item">
                    <span>Package</span>
                    <strong>
                      {consultation.package?.name || "-"}
                    </strong>
                  </div>

                  <div className="consultation-info-item">
                    <span>Service</span>
                    <strong>
                      {consultation.service?.name || "-"}
                    </strong>
                  </div>

                  <div className="consultation-info-item">
                    <span>Technician</span>
                    <strong>
                      {consultation.technician?.name || "-"}
                    </strong>
                  </div>

                </div>

                {/* Action */}
                <div className="consultation-card-actions">

                  <button
                    type="button"
                    className={`consultation-open-button ${
                      isOngoing
                        ? "button-ongoing"
                        : ""
                    }`}
                    onClick={() =>
                      handleOpen(consultation)
                    }
                  >
                    <span>
                      {isOngoing
                        ? "Join Consultation"
                        : "Open Consultation"}
                    </span>

                    <span className="button-arrow">
                      →
                    </span>
                  </button>

                </div>

              </div>
            );
          })}

        </div>
      )}

    </div>
  );
};

export default DoctorTeleConsultationsPage;
